import { squaresFor, squares3D, heightSquares } from '../grid/measure.js';
import { bonusTypeKey } from '../rules/modifiers.js';
import { footprintSquares } from './fog.js';
import { caughtIn } from './caught.js';
export const AURA_TARGETS = ['attack', 'ac', 'save'];
export const AURA_APPLIES = ['allies', 'enemies', 'all'];
export const AURA_LIMITS = { auras: 4, mods: 6, value: 20, label: 60, radius: 40, type: 63 };
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const COLOUR_RE = /^#[0-9a-f]{6}$/;
const TYPE_RE = new RegExp(`^[a-z][a-z0-9_]{0,${AURA_LIMITS.type - 1}}$`);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function parseMod(v) {
    if (!isObj(v))
        return null;
    const { to, value, type } = v;
    if (!AURA_TARGETS.includes(to))
        return null;
    if (typeof value !== 'number' || !Number.isInteger(value) || value === 0 || Math.abs(value) > AURA_LIMITS.value)
        return null;
    // Any well-formed type here: whether it is one of the system's is checked as it is committed (`auraTypeRefusal`).
    const key = typeof type === 'string' ? bonusTypeKey(type) : undefined;
    if (type !== undefined && (key === undefined || !TYPE_RE.test(key)))
        return null;
    return { to: to, value, ...(key !== undefined && key !== 'untyped' ? { type: key } : {}) };
}
export function parseAura(v) {
    if (!isObj(v))
        return null;
    const { id, label, radius, colour, applies, mods } = v;
    if (typeof id !== 'string' || !ID_RE.test(id))
        return null;
    if (typeof label !== 'string' || !label.trim() || label.length > AURA_LIMITS.label || CONTROL_RE.test(label))
        return null;
    if (typeof radius !== 'number' || !Number.isInteger(radius) || radius < 1 || radius > AURA_LIMITS.radius)
        return null;
    if (colour !== undefined && (typeof colour !== 'string' || !COLOUR_RE.test(colour)))
        return null;
    if (!AURA_APPLIES.includes(applies))
        return null;
    if (!Array.isArray(mods) || mods.length > AURA_LIMITS.mods)
        return null;
    const out = [];
    for (const m of mods) {
        const p = parseMod(m);
        if (!p)
            return null;
        out.push(p);
    }
    return { id, label: label.trim(), radius, ...(colour !== undefined ? { colour } : {}), applies: applies, mods: out };
}
/** A token's whole list: at most four, ids distinct; null for anything else. */
export function parseAuras(v) {
    if (!Array.isArray(v) || v.length > AURA_LIMITS.auras)
        return null;
    const out = [];
    for (const a of v) {
        const p = parseAura(a);
        if (!p)
            return null;
        out.push(p);
    }
    return new Set(out.map((a) => a.id)).size === out.length ? out : null;
}
export const copyAuras = (auras) => auras.map((a) => ({ ...a, mods: a.mods.map((m) => ({ ...m })) }));
/** Why a list cannot be kept at this table: a bonus type its system does not have (as an effect's, issue 1303). Null: fine. */
export function auraTypeRefusal(auras, types) {
    for (const a of auras)
        for (const m of a.mods) {
            if (m.type && !types.includes(bonusTypeKey(m.type)))
                return `${m.type.replace(/_/g, ' ')} is not a bonus type of this game system.`;
        }
    return null;
}
/** Whether square (i, j) is in `aura` around a token whose space is `box`: within its radius by the diagonal rule. */
export function inAura(box, radius, diagonal, i, j, dz = 0) {
    const dx = Math.max(0, box.i0 - i, i - box.i1);
    const dy = Math.max(0, box.j0 - j, j - box.j1);
    return (dz ? squares3D(dx, dy, dz, diagonal) : squaresFor(dx, dy, diagonal)) <= radius;
}
/** The tokens inside an aura of `holder`'s, by the system's area rule (null: none), as an area's list is made. */
export function insideAura(holder, radius, objects, grid, extent, diagonal, includes, height) {
    if (!includes)
        return [];
    const box = footprintSquares(grid, extent, { x: holder.x, y: holder.y }, holder.size);
    const flat = caughtIn((i, j) => inAura(box, radius, diagonal, i, j), includes, objects, grid, extent);
    if (!height)
        return flat;
    // Height (qa-runs#1160; design/32 §3): a token above or below the holder, by the height between them.
    const hz = height(holder);
    return flat.filter((o) => {
        const dz = heightSquares(height(o) - hz, diagonal);
        return !dz || caughtIn((i, j) => inAura(box, radius, diagonal, i, j, dz), includes, [o], grid, extent).length > 0;
    });
}
/**
 * Whether `other` is an ally of `holder` for an aura (issue 1150; the sides are issue 1046's disposition, the DM's
 * alone, read on the server). The party — player characters and any token a player owns — are each other's allies,
 * whatever side the DM set. Otherwise each token's side is its disposition, and with none set a party token is a
 * friend and everything else a foe; the same side is an ally. So with no sides set at all, a player character's
 * `allies` means the player characters and their owners' tokens, and `enemies` everything else. A token is its own ally.
 */
export const allied = (holder, other) => relation(holder, other) === 'ally';
/**
 * The side rule's three answers (qa-runs#1157): allies as `allied` says; otherwise a Neutral on either side makes them
 * neither allies nor enemies, and a friend and a foe are enemies. With no Neutral set this is exactly `!allied`.
 */
export function relation(holder, other) {
    if (holder.id === other.id)
        return 'ally';
    const party = (o) => o.pc || o.owners.length > 0;
    if (party(holder) && party(other))
        return 'ally';
    const side = (o) => o.disposition ?? (party(o) ? 'friend' : 'foe');
    const a = side(holder), b = side(other);
    if (a === 'neutral' || b === 'neutral')
        return 'neutral';
    return a === b ? 'ally' : 'enemy';
}
/** Whether an aura touches `other` (qa-runs#1157): everyone's touches all; allies-only and enemies-only pass a Neutral by. */
export const auraApplies = (aura, holder, other) => aura.applies === 'all' || (aura.applies === 'allies' ? 'ally' : 'enemy') === relation(holder, other);
/**
 * Every aura that covers `target` and applies to it (issue 1150), in the order the tokens and their auras are listed.
 * A token the DM hides, or is still placing, holds no aura at the table: hidden means absent, and its aura is part of it
 * (as its light lights nothing, #1151). An aura with no numbers covers no one here — it is only drawn.
 */
export function aurasOver(target, objects, grid, extent, diagonal, includes, height) {
    const out = [];
    for (const holder of objects) {
        if (!holder.auras?.length || holder.hidden || holder.held || holder.kind !== 'token')
            continue;
        for (const aura of holder.auras) {
            if (!aura.mods.length || !auraApplies(aura, holder, target))
                continue;
            if (insideAura(holder, aura.radius, [target], grid, extent, diagonal, includes, height).length)
                out.push({ holder, aura });
        }
    }
    return out;
}
/** An aura's term label: "Aura of Courage (aura)". */
export const auraLabel = (a) => `${a.label} (aura)`;
//# sourceMappingURL=auras.js.map